import { CanvasStartFlow } from "./CanvasStartFlow";
import { NodeType, Position } from "./types";

export class CanvasNode {
    id: string;
    type: string;
    position: Position;
    data: CanvasNodeData | CanvasStartFlow;

    constructor(json) {
        this.id = crypto.randomUUID();
        this.type = "custom";
        this.position = { x: Math.random() * 400, y: Math.random() * 400 };
        this.data = new CanvasNodeData(json);
    }
}
export class CanvasNodeData {
    id: string;
    isDrag: boolean;
    name: string;
    icon: string;
    type: NodeType;
    canAddContent: boolean;
    backLinkEnabled: boolean;
    content: CanvasNodeContent[];

    /**
     * Marks this step as a conversion goal, so analytics can report how many people reached it
     * rather than only how many reached the end of the flow. Reaching the last box says nothing
     * about whether anything valuable happened — a "No thanks" branch ends the flow too.
     *
     * Node-level rather than inside content[].data on purpose: that shape is per node type and
     * would mean touching the getData() switch for every type. Here it works on any node, with
     * no new node type, no palette entry, and no extra case in either backend switch.
     */
    isGoal?: boolean;
    goalName?: string;

    constructor(json) {
        this.id = crypto.randomUUID();
        this.type = json?.["type"] as NodeType;
        this.name = json?.["name"];
        this.icon = json?.["icon"];
        this.isDrag = true;
        this.canAddContent = json?.["canAddContent"];
        this.backLinkEnabled = json?.["backLinkEnabled"];
        this.content = [new CanvasNodeContent(json)];
    }
}
export class CanvasNodeContent {
    id: string;
    type: string;
    data: Object | null;

    private getData(type: NodeType) {
        switch (type) {
            case "FLOW_START":
                return {
                    keywords: [],
                    templates: [],
                    regexCaseSensitive: false,
                    sheetTriggerEnabled: false,
                    qrTriggerEnabled: false,
                    triggerType: "KEYWORD",
                    adSourceIds: [],
                    fbLeadTriggerEnabled: false
                }
            case "FLOW":
                return {
                    flowId: "",
                    flowName: ""
                }
            case 'TEXT_BUTTON':
                return {
                    text: "",
                    buttons: []
                }
            case "MEDIA_BUTTON":
                return {
                    url: "",
                    text: "",
                    fileType: "",
                    fileName: "",
                    buttons: []
                }
            case "LIST":
                return {
                    mode: "static",
                    header: "",
                    body: "",
                    footer: "",
                    buttonText: "",
                    saveAs: "",
                    sections: [],
                    arrayAttribute: "",
                    titlePath: "",
                    descPath: "",
                    sampleJson: ""
                }
            case "CATELOGUE_MESSAGE":
                return {
                    body: "",
                    footer: ""
                }
            // `products`, not `items` — FlowAnalyticsService walks sections[].items[] for list rows.
            case "PRODUCT_LIST":
                return {
                    header: "",
                    body: "",
                    footer: "",
                    saveAs: "",
                    catalogId: "",
                    sections: []
                }
            case "LOCATION":
                return {
                    body: "",
                    buttons: []
                }
            case "QUESTION":
                return {
                    attribute: "",
                    attributeFormat: "",
                    attributeNumberOfAttempt: "",
                    text: "",
                    errorMessage: "",
                    attributeFormatValue: {
                        min: "",
                        max: "",
                        regex: ""
                    }
                }
            case "CONDITION":
                return {
                    condition: "",
                    attribute: "",
                    trueNode: "",
                    falseNode: ""
                }
            case "TEMPLATE":
                return {
                    bodyText: "",
                    footerText: "",
                    headerText: "",
                    templateName: "",
                    quickReplies: [],
                    buttons: [],
                    callToAction: [],
                    formattedButtons: [],
                    parameters: 0,
                    sampleMessage: "",
                    status: "",
                    templateId: "",
                    templateLanguage: "",
                    templateParams: [],
                    type: "",
                }
            case "MEDIA":
                return {
                    attribute: "",
                    attributeFormat: "",
                    attributeNumberOfAttempt: 0,
                    mediaType: "",
                    text: "",
                    attributeFormatValidationErrorMessage: ""
                }
            case "ATTRIBUTE":
                return {
                    attribute: "",
                    value: ""
                }
            case "ADD_TAG":
                return {
                    tags: []
                }
            case "ADDRESS":
                return {
                    requestAttribute: "",
                    responseAttribute: "",
                    text: ""
                }
            case "SINGLE_PRODUCT":
                return {
                    body: "",
                    footer: "",
                    saveAs: "",
                    catalogId: "",
                    productRetailerId: "",
                    product: null
                }
            case "API_REQUEST":
                return {
                    statusCodes: [],
                    requestObject: {},
                    capturingAttributes: {}
                }
            case "CAROUSEL":
                return {
                    arrayAttribute: "",
                    titlePath: "",
                    descPath: "",
                    imagePath: "",
                    text: "Check out these options!",
                    buttons: []
                }
            case "SMART_DELAY":
                return {
                    delayValue: 1,
                    delayUnit: "HOURS"
                }
            case "GOOGLE_SHEETS":
                return {
                    operation: "APPEND",
                    spreadsheetId: "",
                    sheetName: "",
                    lookupColumn: "",
                    lookupValue: "",
                    writeMapping: [],
                    readCapture: [],
                    rowStart: 1,
                    rowEnd: 10,
                    filterColumn: "",
                    filterValue: "",
                    arrayAttributeName: "",
                    successNode: crypto.randomUUID(),
                    failureNode: crypto.randomUUID()
                }
            default:
                return null;
        }
    }

    constructor(json) {
        this.id = (!!json?.["id"]) ? json?.["id"] : crypto.randomUUID();
        this.type = json?.["type"];
        this.data = this.getData(this.type as NodeType);
        if (this.type == "FLOW_START" && !!json?.["keywords"]) {
            (this.data as any).keywords = json?.["keywords"];
        }
    }
}